import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Parcelas a pagar da empresa ativa (Sprint 2 do cap mensal). Lê todas as
 * parcelas em aberto/pagas dos últimos 180 dias até +365 dias à frente —
 * janela suficiente para o KPI "Caixa do mês" e o forecast 30/60/90.
 */
export interface Installment {
  id: string
  purchase_order_id: string
  due_date: string  // YYYY-MM-DD
  amount: number
  paid_at: string | null
}

export function useInstallments() {
  const [installments, setInstallments] = useState<Installment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const dayMs = 86_400_000
    const start = new Date(Date.now() - 180 * dayMs).toISOString().slice(0, 10)
    const end = new Date(Date.now() + 365 * dayMs).toISOString().slice(0, 10)
    const { data, error: err } = await supabase
      .from('order_installments')
      .select('id, purchase_order_id, due_date, amount, paid_at')
      .gte('due_date', start)
      .lte('due_date', end)
      .order('due_date', { ascending: true })
    if (err) { setError(err.message); setLoading(false); return }
    setInstallments(((data ?? []) as Array<{
      id: string; purchase_order_id: string; due_date: string;
      amount: number | string; paid_at: string | null
    }>).map(r => ({
      id: r.id,
      purchase_order_id: r.purchase_order_id,
      due_date: r.due_date,
      amount: Number(r.amount),
      paid_at: r.paid_at,
    })))
    setLoading(false)
  }, [])

  useEffect(() => { void fetchData() }, [fetchData])

  /** Soma das parcelas com due_date no mês `monthKey` (YYYY-MM). */
  const sumByMonth = useCallback((monthKey: string): number => {
    let s = 0
    for (const i of installments) {
      if (i.due_date.slice(0, 7) === monthKey) s += i.amount
    }
    return s
  }, [installments])

  /**
   * Soma das parcelas que vencem nos próximos `days` dias (a partir de hoje,
   * inclusive). Usado pelos cards de forecast 30/60/90.
   */
  const sumNext = useCallback((days: number, ref = new Date()): number => {
    const dayMs = 86_400_000
    const startKey = ref.toISOString().slice(0, 10)
    const endKey = new Date(ref.getTime() + days * dayMs).toISOString().slice(0, 10)
    let s = 0
    for (const i of installments) {
      if (i.due_date >= startKey && i.due_date <= endKey) s += i.amount
    }
    return s
  }, [installments])

  /** Quebrado por mês (YYYY-MM → total) para forecast tabular. */
  const byMonth = useMemo(() => {
    const map = new Map<string, number>()
    for (const i of installments) {
      const mk = i.due_date.slice(0, 7)
      map.set(mk, (map.get(mk) ?? 0) + i.amount)
    }
    return map
  }, [installments])

  return { installments, loading, error, sumByMonth, sumNext, byMonth, refetch: fetchData }
}
